import camelcaseKeys from 'camelcase-keys'

import type { ArtifactRecord, ChatMessage } from '../types'
import { camelcaseSpec, isArtifactKind } from './artifactKinds'

interface ToolResultPayload {
  artifactId?: string
  kind?: string
  spec?: Record<string, unknown>
  error?: unknown
}

interface ToolErrorPayload {
  errorCode?: string
  code?: string
}

export interface ToolResultStatus {
  isError: boolean
  errorCode?: string | null
  payload?: unknown
}

export interface ToolOutputs {
  artifacts: Record<string, ArtifactRecord>
  toolResults: Record<string, ToolResultStatus>
}

// A user message whose blocks are all tool results is an internal protocol
// turn (the tool outputs sent back to the model), not something the user
// typed, so it renders nothing. Single source of truth for that predicate:
// MessageItem suppresses these, and ChatThread skips them when grouping a
// turn's assistant steps. The length guard keeps an empty-content message
// from being misclassified as tool results (`[].every` is vacuously true).
export function isToolResultMessage(message: ChatMessage): boolean {
  return (
    message.role === 'user' &&
    message.contentJson.length > 0 &&
    message.contentJson.every((block) => block.type === 'tool_result')
  )
}

export function extractToolOutputsForAssistantMessage(
  messages: ChatMessage[],
  assistantIndex: number,
): ToolOutputs {
  const artifacts: Record<string, ArtifactRecord> = {}
  const toolResults: Record<string, ToolResultStatus> = {}

  const next = messages[assistantIndex + 1]
  if (!next || next.role !== 'user') return { artifacts, toolResults }

  for (const block of next.contentJson) {
    if (block.type !== 'tool_result') continue

    let parsed: Record<string, unknown> | null = null
    try {
      parsed = JSON.parse(block.content) as Record<string, unknown>
    } catch {
      parsed = null
    }

    toolResults[block.tool_use_id] = buildToolResultStatus(
      block.is_error,
      parsed,
      block.content,
    )

    if (block.is_error || parsed === null) continue
    const artifact = buildArtifactRecord(block.tool_use_id, parsed)
    if (artifact) artifacts[block.tool_use_id] = artifact
  }

  return { artifacts, toolResults }
}

function buildToolResultStatus(
  isError: boolean | undefined,
  parsed: Record<string, unknown> | null,
  raw: string,
): ToolResultStatus {
  let errorCode: string | null = null
  if (isError && parsed) {
    const error = camelcaseKeys(parsed) as ToolErrorPayload
    errorCode = error.errorCode ?? error.code ?? null
  }
  return {
    isError: Boolean(isError),
    errorCode,
    payload: parsed ?? raw,
  }
}

function buildArtifactRecord(
  toolUseId: string,
  raw: Record<string, unknown>,
): ArtifactRecord | null {
  const parsed = camelcaseKeys(raw) as ToolResultPayload
  if (!parsed.kind || !parsed.spec) return null
  if (!isArtifactKind(parsed.kind)) return null
  return {
    toolUseId,
    artifactId: parsed.artifactId ?? '',
    kind: parsed.kind,
    spec: camelcaseSpec(parsed.kind, parsed.spec),
  }
}
